import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Multiplier, Text, SegmentedButtons } from '@Sage/hoi-poi-ui';

import ModalHoi from 'components/ModalHoi';
import HtmlPreviewFrame from 'containers/components/HtmlPreviewFrame';
import { REG_EX_EMAIL } from 'constants/Constants';
import { getDateFromBackendISO } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import { sendTestEmail } from 'services/MailingService';
import { logMarketingCampaignsEvent } from '../../utils/marketingTracking';

import './styles.scss';

function getEmailFromMultiplierRow(row) {
    if (row == null) {
        return '';
    }
    if (typeof row === 'string') {
        return row;
    }
    if (typeof row === 'object' && !Array.isArray(row)) {
        const v = row.email;
        return v == null ? '' : String(v);
    }
    return '';
}

function normalizeDefaultRecipients(defaultRecipient) {
    if (defaultRecipient == null || defaultRecipient === '') {
        return [''];
    }
    if (Array.isArray(defaultRecipient)) {
        const emails = defaultRecipient
            .map((r) => getEmailFromMultiplierRow(r).trim())
            .filter(Boolean);
        return emails.length > 0 ? emails : [''];
    }
    const single = String(defaultRecipient).trim();
    return single ? [single] : [''];
}

function resolveSendAtTime(sendAt) {
    if (sendAt == null) {
        return null;
    }
    if (typeof sendAt === 'string') {
        const trimmed = sendAt.trim();
        return trimmed || null;
    }
    if (sendAt instanceof Date) {
        return Number.isNaN(sendAt.getTime()) ? null : sendAt.toISOString();
    }
    if (typeof sendAt === 'object' && typeof sendAt.time === 'string') {
        const trimmed = sendAt.time.trim();
        return trimmed || null;
    }
    return null;
}

function formatSendAtDisplay(sendAt) {
    const time = resolveSendAtTime(sendAt);
    if (!time) {
        return '';
    }
    const datePart = getDateFromBackendISO(time, 'LL');
    const timePart = getDateFromBackendISO(time, 'LT');
    return getLiteralWithParameters('label_date_with_time', [datePart, timePart]);
}

const CampaignPreviewTestModal = memo(
    ({
        isOpen,
        onRequestClose,
        onAfterClose,
        from,
        subject,
        sendAt,
        previewText,
        htmlPreview,
        defaultRecipient,
        onSendTestEmail,
        withoutSend = false,
    }) => {
        const [previewMode, setPreviewMode] = useState('web');
        const [recipients, setRecipients] = useState(() =>
            normalizeDefaultRecipients(defaultRecipient),
        );
        const [recipientFieldErrors, setRecipientFieldErrors] = useState([]);
        const [isLoading, setIsLoading] = useState(false);

        const emailRegex = useMemo(() => new RegExp(REG_EX_EMAIL.source, 'i'), []);
        const hasSender = Boolean(from);
        const formattedSendAt = useMemo(() => formatSendAtDisplay(sendAt), [sendAt]);
        const showTestColumn = hasSender && !withoutSend;
        const showPreviewMeta = hasSender || Boolean(formattedSendAt);

        useEffect(() => {
            if (!isOpen) {
                return;
            }

            logMarketingCampaignsEvent({ submodule: 'campaign', functionality: 'preview' });
            setPreviewMode('web');
            setRecipients(normalizeDefaultRecipients(defaultRecipient));
            setRecipientFieldErrors([]);
        }, [isOpen, defaultRecipient]);

        const handleClose = useCallback(() => {
            setRecipientFieldErrors([]);
            onRequestClose?.();
        }, [onRequestClose]);

        const handlePreviewModeChange = useCallback((mode) => {
            setPreviewMode(mode);
        }, []);

        const handleRecipientsChange = useCallback((values) => {
            setRecipients(values);
            setRecipientFieldErrors([]);
        }, []);

        const validateRecipients = useCallback(() => {
            if (!recipients || recipients.length === 0) {
                setRecipientFieldErrors([getLiteral('helptext_required_field')]);
                return false;
            }

            const fieldErrors = recipients.map((r) => {
                const trimmed = getEmailFromMultiplierRow(r).trim();
                if (!trimmed) {
                    return getLiteral('helptext_required_field');
                }
                if (!emailRegex.test(trimmed)) {
                    return getLiteral('error_invalid_email_format');
                }
                return '';
            });

            const hasAnyError = fieldErrors.some(Boolean);
            setRecipientFieldErrors(hasAnyError ? fieldErrors : []);
            return !hasAnyError;
        }, [emailRegex, recipients]);

        const handleSendTestEmail = useCallback(async () => {
            if (!validateRecipients()) {
                return;
            }

            const trimmedEmails = recipients
                .map((r) => getEmailFromMultiplierRow(r).trim())
                .filter(Boolean);

            const payload = {
                to: trimmedEmails,
                from,
                subject,
                previewText,
                body: htmlPreview,
            };

            setIsLoading(true);
            sendTestEmail(payload)
                .then(() => {
                    logMarketingCampaignsEvent({ functionality: 'sendTestEmail' });
                    successToast({
                        text: getLiteral('label_toast_marketing_campaigns_send_email_success'),
                    });
                    onSendTestEmail?.(trimmedEmails);
                })
                .catch(() => {
                    errorToast({ text: getLiteral('error_generalerror') });
                })
                .finally(() => setIsLoading(false));
        }, [
            from,
            htmlPreview,
            onSendTestEmail,
            previewText,
            recipients,
            subject,
            validateRecipients,
        ]);

        return (
            <ModalHoi
                className={classNames('campaign-preview-test-modal', {
                    'campaign-preview-test-modal--no-sender': !showTestColumn,
                })}
                isOpen={isOpen}
                onRequestClose={handleClose}
                onAfterClose={onAfterClose}
                title={getLiteral(
                    showTestColumn
                        ? 'label_marketing_campaigns_preview_and_test'
                        : 'label_marketing_campaigns_email_preview_modal',
                )}
                useHeader
                useCornerClose
                size="full"
            >
                <div className="campaign-preview-test-modal__content">
                    <div className="campaign-preview-test-modal__preview-column">
                        <div className="campaign-preview-test-modal__preview__header">
                            {showPreviewMeta && (
                                <div className="campaign-preview-test-modal__header-left">
                                    {hasSender && (
                                        <div className="campaign-preview-test-modal__row">
                                            <Text type="body" medium>
                                                {`${getLiteral('label_from')}:`}
                                            </Text>
                                            <Text type="body">{from || '-'}</Text>
                                        </div>
                                    )}
                                    {hasSender && (
                                        <div className="campaign-preview-test-modal__row">
                                            <Text type="body" medium>
                                                {`${getLiteral('label_subject')}:`}
                                            </Text>
                                            <Text type="body">{subject || '-'}</Text>
                                        </div>
                                    )}
                                    {formattedSendAt ? (
                                        <div className="campaign-preview-test-modal__row">
                                            <Text type="body" medium>
                                                {`${getLiteral('label_marketing_campaigns_sent')}:`}
                                            </Text>
                                            <Text type="body">{formattedSendAt}</Text>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                            <div
                                className={classNames('campaign-preview-test-modal__header-right', {
                                    'campaign-preview-test-modal__header-right--full':
                                        !showPreviewMeta,
                                })}
                            >
                                <SegmentedButtons
                                    options={[
                                        { name: 'web', icon: 'desktop' },
                                        { name: 'mobile', icon: 'mobile' },
                                    ]}
                                    value={previewMode}
                                    onChange={handlePreviewModeChange}
                                />
                            </div>
                        </div>

                        <div
                            className={classNames('campaign-preview-test-modal__html-preview', {
                                'campaign-preview-test-modal__html-preview--mobile':
                                    previewMode === 'mobile',
                            })}
                        >
                            <HtmlPreviewFrame
                                html={htmlPreview}
                                title={getLiteral('label_marketing_campaigns_preview_and_test')}
                                withInteraction
                            />
                        </div>
                    </div>

                    {showTestColumn && (
                        <div className="campaign-preview-test-modal__form-column">
                            <Text type="h6">
                                {getLiteral(
                                    'label_marketing_campaigns_preview_test_recipient_heading',
                                )}
                            </Text>
                            <Text type="body" className="campaign-preview-test-modal__description">
                                {getLiteral(
                                    'label_marketing_campaigns_preview_test_recipient_desc',
                                )}
                            </Text>

                            <Multiplier
                                isFullWidth
                                labelMode="vertical"
                                schema={{
                                    label: `${getLiteral('label_recipient')}*`,
                                    name: 'email',
                                    type: 'text',
                                    placeholder: getLiteral('placeholder_enter_email_address'),
                                }}
                                value={recipients}
                                onChange={handleRecipientsChange}
                                error={recipientFieldErrors}
                                buttonLabel={getLiteral('label_add_email')}
                                max={4}
                            />

                            <Button
                                className="campaign-preview-test-modal__send-test-email-button"
                                type="primary"
                                onClick={handleSendTestEmail}
                                isLoading={isLoading}
                            >
                                {getLiteral('action_marketing_campaigns_send_test_email')}
                            </Button>
                        </div>
                    )}
                </div>
            </ModalHoi>
        );
    },
);

CampaignPreviewTestModal.displayName = 'CampaignPreviewTestModal';

CampaignPreviewTestModal.propTypes = {
    isOpen: PropTypes.bool,
    onRequestClose: PropTypes.func,
    onAfterClose: PropTypes.func,
    from: PropTypes.string,
    subject: PropTypes.string,
    sendAt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
        PropTypes.shape({ time: PropTypes.string }),
    ]),
    previewText: PropTypes.string,
    withoutSend: PropTypes.bool,
    htmlPreview: PropTypes.string,
    defaultRecipient: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    onSendTestEmail: PropTypes.func,
};

export default CampaignPreviewTestModal;
